import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Active draft-sabotage effect, mirrored from public.nfl_draft_sabotage_events.
 * `effect_data` is whatever the play_card RPC stored (e.g. a fake injury name).
 */
export interface DraftSabotageEffect {
  id: string;
  league_id: string;
  card_id: string;
  attacker_team_id: string;
  target_team_id: string | null;
  round: number | null;
  pick_number: number | null;
  effect_type: string;
  effect_data: Record<string, any>;
  active: boolean;
  expires_at_pick: number | null;
  expires_at_round: number | null;
  created_at: string;
}

export interface SabotageCardLite {
  id: string;
  name: string;
  emoji: string | null;
  rarity: string;
}

interface UseDraftSabotageEffectsOptions {
  leagueId: string | null | undefined;
  myTeamId: string | null | undefined;
  draftStatus: string | null;
  /** Called when current user's team is the target (e.g. trigger screen shake). */
  onSabotaged?: (effect: DraftSabotageEffect, card: SabotageCardLite | null) => void;
  /** Called on every new event so the page can render a toast. */
  onEvent?: (effect: DraftSabotageEffect, card: SabotageCardLite | null) => void;
}

/**
 * Subscribes to nfl_draft_sabotage_events for the league and tracks the
 * currently active effects. Also exposes a draft-card lookup so toasts/banners
 * can render a card name + emoji without an extra round-trip.
 */
export function useDraftSabotageEffects({
  leagueId,
  myTeamId,
  draftStatus,
  onSabotaged,
  onEvent,
}: UseDraftSabotageEffectsOptions) {
  const [activeEffects, setActiveEffects] = useState<DraftSabotageEffect[]>([]);
  const [cardMap, setCardMap] = useState<Record<string, SabotageCardLite>>({});

  // Stable refs so the subscription effect doesn't re-subscribe every render
  // when the parent passes inline closures.
  const onSabotagedRef = useRef(onSabotaged);
  const onEventRef = useRef(onEvent);
  useEffect(() => { onSabotagedRef.current = onSabotaged; }, [onSabotaged]);
  useEffect(() => { onEventRef.current = onEvent; }, [onEvent]);

  // Fetch the draft-card catalog once so we can resolve card_id → name.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await (supabase as any)
        .from('nfl_sabotage_cards')
        .select('id, name, emoji, rarity')
        .eq('card_type', 'draft');
      if (cancelled) return;
      const map: Record<string, SabotageCardLite> = {};
      for (const c of (data || [])) map[c.id] = c;
      setCardMap(map);
    })();
    return () => { cancelled = true; };
  }, []);

  const refetchActive = useCallback(async () => {
    if (!leagueId) { setActiveEffects([]); return; }
    if (draftStatus !== 'in_progress') { setActiveEffects([]); return; }
    try {
      const { data, error } = await (supabase as any).functions.invoke('nfl-draft-sabotage', {
        body: { action: 'get_active', league_id: leagueId },
      });
      if (error) {
        // Non-members get a 403 — that's fine, they have no effects to render.
        setActiveEffects([]);
        return;
      }
      setActiveEffects((data?.events || []) as DraftSabotageEffect[]);
    } catch {
      setActiveEffects([]);
    }
  }, [leagueId, draftStatus]);

  // Initial fetch + refetch when leagueId / draft status changes.
  useEffect(() => { refetchActive(); }, [refetchActive]);

  // Realtime subscription for INSERT (new card played) and UPDATE (active
  // flipped to false by the make-pick sweep). DELETE is unused — we only
  // soft-delete via active=false.
  useEffect(() => {
    if (!leagueId || draftStatus !== 'in_progress') return;

    const channel = (supabase as any)
      .channel(`draft-sabotage-${leagueId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'nfl_draft_sabotage_events',
          filter: `league_id=eq.${leagueId}`,
        },
        (payload: any) => {
          const evt = payload.new as DraftSabotageEffect;
          if (evt.active) {
            setActiveEffects((prev) => [...prev, evt]);
          }
          const card = cardMap[evt.card_id] || null;
          onEventRef.current?.(evt, card);
          if (myTeamId && evt.target_team_id === myTeamId) {
            onSabotagedRef.current?.(evt, card);
          }
        },
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'nfl_draft_sabotage_events',
          filter: `league_id=eq.${leagueId}`,
        },
        (payload: any) => {
          const evt = payload.new as DraftSabotageEffect;
          setActiveEffects((prev) => {
            if (!evt.active) return prev.filter((e) => e.id !== evt.id);
            // Replace if exists, otherwise append (rare — INSERT usually
            // gets there first).
            const idx = prev.findIndex((e) => e.id === evt.id);
            if (idx === -1) return [...prev, evt];
            const next = [...prev];
            next[idx] = evt;
            return next;
          });
        },
      )
      .subscribe();

    return () => { (supabase as any).removeChannel(channel); };
  }, [leagueId, draftStatus, myTeamId, cardMap]);

  return { activeEffects, cardMap, refetchActive };
}
